import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BaseButton } from './BaseButton';

export interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  onSubmit?: () => void | Promise<void>;
  isLoading?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  showFooter?: boolean;
  paperSx?: object;
  contentSx?: object;
}

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  maxWidth = 'md',
  fullWidth = true,
  children,
  actions,
  onSubmit,
  isLoading = false,
  submitButtonText = 'Submit',
  cancelButtonText = 'Cancel',
  showFooter = true,
  paperSx,
  contentSx,
}) => {
  const handleSubmit = async () => {
    if (onSubmit) {
      try {
        await onSubmit();
      } catch (err) {
        console.error('Modal submit error:', err);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          ...paperSx,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: '1.25rem',
          color: '#333333',
          borderBottom: '1px solid #E6E7EB',
        }}
      >
        {title}
        <BaseButton
          onClick={onClose}
          disabled={isLoading}
          variant="text"
          colorVariant="secondary"
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </BaseButton>
      </DialogTitle>

      <DialogContent
        sx={{
          '&&': {
            paddingTop: '32px',
            paddingBottom: '24px',
          },
          minHeight: '300px',
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {showFooter && (
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid #E6E7EB',
            gap: 1,
            backgroundColor: '#F2F3F5',
          }}
        >
          {actions || (
            <>
              <BaseButton
                onClick={onClose}
                disabled={isLoading}
                variant="outlined"
                colorVariant="secondary"
              >
                {cancelButtonText}
              </BaseButton>
              <BaseButton
                onClick={handleSubmit}
                disabled={isLoading}
                variant="contained"
                colorVariant="primary"
                isLoading={isLoading}
                loadingLabel="Saving..."
              >
                {submitButtonText}
              </BaseButton>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseModal;
